<template>
  <section class="settings-card timezone-settings">
    <div class="settings-card-header">
      <div class="settings-card-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3.5 2" />
        </svg>
      </div>
      <div class="settings-card-heading">
        <h3>Timezone</h3>
        <p>
          Set the timezone job sites use for clock-in/out times — company-wide,
          or per project.
        </p>
      </div>
    </div>

    <div class="settings-form">
      <b-row class="settings-fields">
        <b-col cols="12" class="scope-col">
          <b-form-group label="Applies to" label-class="form-label-sm">
            <div class="scope-field">
              <b-form-select
                v-model="selectedProjectId"
                :options="projectSelectOptions"
              />
              <span
                v-if="selectedProjectId !== 'all'"
                class="scope-badge"
                :class="isCustomContext ? 'is-override' : 'is-default'"
              >
                {{ scopeBadgeLabel }}
              </span>
            </div>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Timezone" label-class="form-label-sm">
            <b-form-select v-model="form.timezone" :options="zoneOptions" />
          </b-form-group>
          <p v-if="browserSuggestionHint" class="browser-tz-hint">
            {{ browserSuggestionHint }}
            <button
              type="button"
              class="browser-tz-use"
              @click="useBrowserTimezone"
            >
              Use this
            </button>
          </p>
        </b-col>
      </b-row>

      <p class="scope-hint">{{ contextHint }}</p>

      <div class="settings-footer">
        <div class="footer-buttons">
          <b-button
            v-if="isCustomContext"
            type="button"
            variant="outline-secondary"
            class="reset-button"
            :disabled="saving"
            @click="resetToDefault"
          >
            Reset to default
          </b-button>
          <b-button
            type="button"
            variant="primary"
            class="save-button"
            :disabled="saving"
            @click="save"
          >
            <span v-if="saving" class="button-spinner" aria-hidden="true" />
            {{ saving ? 'Saving…' : saveLabel }}
          </b-button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { BUSINESS_TZ } from '@/utils/timesheetHelpers'

// Curated, not exhaustive — this platform is AU-only today (see the
// per-site-timezones design doc's Phase 0 audit), so a full IANA list would
// just be noise. Covers every state/territory's own zone.
const AU_TIMEZONES = [
  { value: 'Australia/Perth', text: 'Perth — Western Australia (AWST)' },
  { value: 'Australia/Darwin', text: 'Darwin — Northern Territory (ACST)' },
  {
    value: 'Australia/Adelaide',
    text: 'Adelaide — South Australia (ACST/ACDT)',
  },
  { value: 'Australia/Brisbane', text: 'Brisbane — Queensland (AEST)' },
  {
    value: 'Australia/Sydney',
    text: 'Sydney — New South Wales (AEST/AEDT)',
  },
  { value: 'Australia/Melbourne', text: 'Melbourne — Victoria (AEST/AEDT)' },
  { value: 'Australia/Hobart', text: 'Hobart — Tasmania (AEST/AEDT)' },
]

const CONFIRM_MESSAGE =
  'Applies to entries from now on. Existing entries keep their times.'

function firstValidationError(err) {
  var data = (err && err.data) || (err && err.response && err.response.data)
  var errors = data && data.errors
  return errors && errors.timezone && errors.timezone[0]
}

export default {
  name: 'TimezoneSettingsCard',
  data() {
    return {
      saving: false,
      selectedProjectId: 'all',
      zoneOptions: AU_TIMEZONES,
      form: {
        timezone: BUSINESS_TZ,
      },
    }
  },
  computed: {
    company() {
      return this.$store.getters['company/company']
    },
    projects() {
      return this.$store.getters['project/projects'] || []
    },
    companyTimezone() {
      return (this.company && this.company.timezone) || BUSINESS_TZ
    },
    // "All projects" first, then every project. A project on its own
    // timezone gets a "— Custom" marker so the whole list is scannable at a
    // glance, matching PayPeriodSettingsCard's "Applies to" dropdown.
    projectSelectOptions() {
      return [{ value: 'all', text: 'All projects (company default)' }].concat(
        this.projects.map((p) => ({
          value: p.id,
          text: p.timezone ? p.name + ' — Custom' : p.name,
        })),
      )
    },
    selectedProject() {
      if (this.selectedProjectId === 'all') return null
      return this.projects.find((p) => p.id === this.selectedProjectId) || null
    },
    isCustomContext() {
      return !!(this.selectedProject && this.selectedProject.timezone)
    },
    scopeBadgeLabel() {
      return this.isCustomContext ? 'Custom' : 'Company default'
    },
    saveLabel() {
      return this.selectedProjectId === 'all' ? 'Save Timezone' : 'Save'
    },
    contextHint() {
      var companyLabel = this.zoneLabel(this.companyTimezone)
      if (this.selectedProjectId === 'all') {
        return 'Every project follows this timezone unless you give it its own — pick a project above to start.'
      }
      var name = this.selectedProject
        ? this.selectedProject.name
        : 'This project'
      if (this.isCustomContext) {
        return (
          '“' +
          name +
          '” has its own timezone. Reset to default puts it back on the company timezone (' +
          companyLabel +
          ').'
        )
      }
      return (
        '“' +
        name +
        '” follows the company timezone (' +
        companyLabel +
        '). Pick a timezone here, then Save, to give it its own — otherwise it keeps following the company.'
      )
    },
    // The browser's own detected zone, suggested only when it's a real AU
    // zone option and not already what's selected — a quiet hint, not a
    // forced default (a BA on a laptop set to the wrong local zone shouldn't
    // silently overwrite a job site's real timezone).
    browserSuggestionHint() {
      var browserTz = this.detectBrowserTimezone()
      if (!browserTz || browserTz === this.form.timezone) return null
      var match = this.zoneOptions.find((z) => z.value === browserTz)
      if (!match) return null
      return 'Your browser is set to ' + match.text.split(' — ')[0] + '.'
    },
  },
  watch: {
    company: {
      immediate: true,
      handler() {
        if (this.selectedProjectId === 'all') this.syncActiveForm()
      },
    },
    selectedProjectId() {
      this.syncActiveForm()
    },
    projects() {
      if (this.selectedProjectId !== 'all') this.syncActiveForm()
    },
  },
  created() {
    if (this.$store.getters['auth/user'] && !this.company) {
      this.$store.dispatch(
        'company/getCompany',
        this.$store.getters['auth/user'].company_id,
      )
    }
  },
  methods: {
    // Re-fetch the shared project list (same params TimesheetDashboardTab
    // loads on mount) so this card's "Applies to" options and scope badge
    // reflect a timezone just saved on one of them — project/updateProject
    // only updates the singular state.project, not this list.
    refreshProjects() {
      return this.$store.dispatch('project/getProjects', {
        limit: 100,
        is_archived: false,
      })
    },
    zoneLabel(tz) {
      var match = this.zoneOptions.find((z) => z.value === tz)
      return match ? match.text.split(' — ')[0] : tz
    },
    detectBrowserTimezone() {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
      } catch (e) {
        return null
      }
    },
    useBrowserTimezone() {
      var browserTz = this.detectBrowserTimezone()
      var match = this.zoneOptions.find((z) => z.value === browserTz)
      if (match) this.form.timezone = match.value
    },
    // Seed the field from whichever scope is selected: the company's
    // timezone for "All projects", the project's own override if it has
    // one, otherwise the company default (so an inheriting project opens on
    // the timezone it's actually following, not blank).
    syncActiveForm() {
      if (this.selectedProjectId === 'all') {
        this.form.timezone = this.companyTimezone
        return
      }
      var row = this.selectedProject
      this.form.timezone = (row && row.timezone) || this.companyTimezone
    },
    async confirmChange() {
      return this.$bvModal.msgBoxConfirm(CONFIRM_MESSAGE, {
        title: 'Update Timezone',
        okTitle: 'Save',
        okVariant: 'primary',
        cancelTitle: 'Cancel',
        centered: true,
      })
    },
    async save() {
      var ok = await this.confirmChange()
      if (!ok) return

      var forProject = this.selectedProjectId !== 'all'
      var name = this.selectedProject ? this.selectedProject.name : ''
      this.saving = true
      try {
        if (forProject) {
          await this.$store.dispatch('project/updateProject', {
            ...this.selectedProject,
            timezone: this.form.timezone,
          })
          // project/updateProject only commits the singular state.project, not
          // the state.projects list this card reads its "Applies to" options
          // and scope badge from — without this, the badge/reset button stay
          // stale (showing the pre-save state) until an unrelated full reload.
          await this.refreshProjects()
        } else {
          await this.$store.dispatch('company/updateCompany', {
            id: this.company.id,
            formData: { ...this.company, timezone: this.form.timezone },
          })
        }
        this.$bvToast.toast(
          forProject ? name + ' timezone saved.' : 'Company timezone saved.',
          { title: 'Success', variant: 'success', solid: true },
        )
        this.$emit('timezone-saved', forProject ? this.selectedProjectId : null)
      } catch (e) {
        this.$bvToast.toast(
          firstValidationError(e) ||
            (forProject
              ? 'Failed to save the project timezone.'
              : 'Failed to save the company timezone.'),
          { title: 'Error', variant: 'danger', solid: true },
        )
      } finally {
        this.saving = false
      }
    },
    async resetToDefault() {
      if (this.selectedProjectId === 'all' || !this.selectedProject) return
      var ok = await this.confirmChange()
      if (!ok) return

      var name = this.selectedProject.name
      this.saving = true
      try {
        await this.$store.dispatch('project/updateProject', {
          ...this.selectedProject,
          timezone: null,
        })
        await this.refreshProjects()
        this.$bvToast.toast(name + ' is back on the company timezone.', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
        this.$emit('timezone-saved', this.selectedProjectId)
      } catch (e) {
        this.$bvToast.toast(
          firstValidationError(e) || 'Failed to reset the project timezone.',
          { title: 'Error', variant: 'danger', solid: true },
        )
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.timezone-settings {
  margin-top: 20px;
}

.settings-card {
  background: #fff;
  border: 1px solid #e8edf3;
  border-radius: 14px;
  overflow: visible;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03),
    0 8px 24px rgba(15, 23, 42, 0.04);
}

.settings-card-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #eef2f6;
  background: #fff;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;

  .settings-card-icon {
    display: flex;
    flex: 0 0 42px;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    color: #087ff5;
    background: #eef7ff;
    border-radius: 11px;

    svg {
      width: 21px;
      height: 21px;
      stroke: currentColor;
      stroke-width: 1.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  .settings-card-heading {
    min-width: 0;
  }

  h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.3;
    color: #172033;
  }

  p {
    margin: 5px 0 0;
    max-width: 610px;
    font-size: 13px;
    line-height: 1.5;
    color: #667085;
  }
}

.settings-form {
  padding: 22px 24px 24px;
  background: #fbfcfe;
  border-bottom-left-radius: 14px;
  border-bottom-right-radius: 14px;
}

.settings-fields {
  margin-right: -8px;
  margin-left: -8px;

  > div {
    padding-right: 8px;
    padding-left: 8px;
  }
}

.settings-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 18px;
  padding-top: 6px;
}

.save-button {
  min-width: 148px;
  min-height: 40px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 700;
  border: 0;
  border-radius: 9px;
  box-shadow: 0 4px 10px rgba(0, 123, 255, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:not(:disabled):hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(0, 123, 255, 0.24);
  }

  &:disabled {
    box-shadow: none;
  }
}

.button-spinner {
  display: inline-block;
  width: 13px;
  height: 13px;
  margin-right: 6px;
  vertical-align: -2px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: settings-spin 0.7s linear infinite;
}

@keyframes settings-spin {
  to {
    transform: rotate(360deg);
  }
}

.scope-col /deep/ .form-group {
  margin-bottom: 6px;
}

.scope-field {
  display: flex;
  align-items: center;
  gap: 12px;

  /deep/ .custom-select {
    flex: 1 1 auto;
    max-width: 460px;
    min-width: 0;
  }
}

.scope-badge {
  flex: 0 0 auto;
  align-self: center;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  padding: 5px 9px;
  border-radius: 999px;

  &.is-default {
    color: #64748b;
    background: #f1f5f9;
  }

  &.is-override {
    color: #0369a1;
    background: #e0f2fe;
  }
}

.scope-hint {
  margin: 0 0 18px;
  font-size: 12.5px;
  line-height: 1.55;
  color: #667085;
}

.browser-tz-hint {
  margin: -10px 0 18px;
  font-size: 12px;
  color: #7b8798;
}

.browser-tz-use {
  padding: 0;
  margin-left: 4px;
  font-size: 12px;
  font-weight: 700;
  color: #087ff5;
  background: none;
  border: 0;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.footer-buttons {
  display: flex;
  gap: 8px;
}

.reset-button {
  min-height: 40px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 700;
  border-radius: 9px;
}

/deep/ .form-label-sm {
  margin-bottom: 7px;
  font-size: 12px;
  font-weight: 700;
  color: #475467;
  text-transform: none;
  letter-spacing: 0;
}

/deep/ .settings-form {
  .form-group {
    margin-bottom: 18px;
  }

  .custom-select {
    height: 44px;
    font-size: 14px;
    color: #243047;
    background-color: #fff;
    border-color: #d7dee8;
    border-radius: 9px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.02);

    &:focus {
      border-color: #80bdff;
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    }
  }
}

@media (max-width: 575.98px) {
  .settings-card-header {
    align-items: flex-start;
    padding: 18px;
  }

  .settings-form {
    padding: 18px;
  }

  .settings-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .footer-buttons {
    width: 100%;
  }

  .footer-buttons .save-button,
  .footer-buttons .reset-button {
    flex: 1 1 0;
  }

  .save-button {
    width: 100%;
  }

  .scope-field {
    flex-wrap: wrap;
  }
}
</style>
